import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SeoHead from "@/components/feature/SeoHead";
import { articles } from "@/mocks/movies";

export default function Article() {
  const { articleSlug } = useParams<{ articleSlug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleSlug]);

  const article = articles.find((a) => a.slug === articleSlug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <i className="ri-error-warning-line text-6xl text-foreground-400 mb-4 block"></i>
            <p className="text-foreground-500 text-lg">文章未找到</p>
            <Link to="/" className="text-primary-500 hover:text-primary-400 text-sm mt-4 inline-block cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const otherArticles = articles
    .filter((a) => a.id !== article.id)
    .slice(0, 2);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishedAt,
    author: {
      "@type": "Organization",
      name: "春夜影院",
    },
  };

  const seoTitle = `${article.title} - 春夜影院`;
  const seoDescription = article.excerpt;
  const seoKeywords = `春夜影院,${article.title},电影资讯,影评,影视新闻`;

  return (
    <>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={`https://www.chunyemovie.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverUrl}
        schema={articleSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: "影视资讯", href: "/" },
                { label: article.title },
              ]}
            />
          </div>

          <article className="max-w-[900px] mx-auto px-4 md:px-6 pb-16">
            {/* Cover Image */}
            <div className="relative aspect-video rounded-lg overflow-hidden mb-8">
              <img
                src={article.coverUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
              <span className="absolute top-4 left-4 bg-primary-500 text-foreground-50 text-xs px-3 py-1 rounded-md">
                {article.category === "news" ? "资讯" : article.category === "feature" ? "专题" : "影评"}
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-foreground-500 text-sm mb-8">
              <span><i className="ri-calendar-line mr-1"></i>{article.publishedAt}</span>
              <span><i className="ri-user-line mr-1"></i>春夜影院编辑部</span>
            </div>

            <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
              {article.content.split("\n\n").map((paragraph, index) => (
                <p key={index} className="mb-4 text-sm">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                <span className="text-foreground-500 text-xs bg-background-100 px-3 py-1 rounded-full">春夜影院</span>
                <span className="text-foreground-500 text-xs bg-background-100 px-3 py-1 rounded-full">电影资讯</span>
                <span className="text-foreground-500 text-xs bg-background-100 px-3 py-1 rounded-full">影视新闻</span>
              </div>
            </div>
          </article>

          {/* Other Articles */}
          {otherArticles.length > 0 && (
            <section className="py-12 bg-background-100">
              <div className="max-w-[900px] mx-auto px-4 md:px-6">
                <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {otherArticles.map((a) => (
                    <Link
                      key={a.id}
                      to={`/article/${a.slug}`}
                      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 cursor-pointer hover:border-primary-500/30 transition-all duration-300"
                    >
                      <div className="relative aspect-video overflow-hidden">
                        <img
                          src={a.coverUrl}
                          alt={a.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-foreground-950 font-semibold text-sm group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
                          {a.title}
                        </h3>
                        <p className="text-foreground-500 text-xs line-clamp-2">{a.excerpt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}